import {
  List,
  ListItemProps,
  ListProps,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  DialogActions,
  Button
} from '@mui/material'
import { Edit } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Form, Formik, FormikHelpers } from 'formik'
import { usePolicy } from '../contexts/PolicyContext'
import useDialog from '../hooks/useDialog'
import useAssetTimestamps from '../hooks/useAssetTimestamps'
import { updateAsset } from '../services/assetService'
import { createSuccessNotification, createErrorNotification } from '../redux/slices/notifications'
import { humanizeTimestamp } from '../utils/date'
import { Asset, AssetInput } from '../types'
import AssetNameListItemButton from './AssetNameListItemButton'
import AssetCompanyListItemButton from './AssetCompanyListItemButton'
import AssetProjectListItemButton from './AssetProjectListItemButton'
import AssetLevelListItemButton from './AssetLevelListItemButton'
import AssetLiftListItemButton from './AssetLiftListItemButton'
import { isAfter, isBefore } from 'date-fns'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { LoadingButton } from '@mui/lab'
import AssetField from '../types/AssetField'
import ImageWithPreview from './ImageWithPreview'
import AssetPhotoDeleteButton from './AssetPhotoDeleteButton'
import AssetPhotoUploadButton from './AssetPhotoUploadButton'
import CopyableListItemButton from './CopyableListItemButton'
export interface AssetDetailsListProps extends ListProps {
  asset: Asset
  fields?: AssetField[]
  editable?: boolean
  afterUpdate?: (asset: Asset) => void
}

export default function AssetDetailsList({
  asset,
  fields = ['name', 'company.name', 'project.name', 'level.name', 'lift.name'],
  editable,
  afterUpdate,
  children,
  ...props
}: AssetDetailsListProps) {
  return (
    <List {...props}>
      {fields.includes('name') && (
        <AssetNameListItemButton
          key="name"
          asset={asset}
          editable={editable}
          afterUpdate={afterUpdate}
        />
      )}

      {fields.includes('company.name') && (
        <AssetCompanyListItemButton
          key="company"
          asset={asset}
          editable={editable}
          afterUpdate={afterUpdate}
        />
      )}

      {fields.includes('project.name') && (
        <AssetProjectListItemButton
          key="project"
          asset={asset}
          editable={editable}
          afterUpdate={afterUpdate}
        />
      )}

      {asset.project && fields.includes('level.name') && (
        <AssetLevelListItemButton
          key="level"
          asset={asset}
          editable={editable}
          afterUpdate={afterUpdate}
        />
      )}

      {asset.project && fields.includes('lift.name') && (
        <AssetLiftListItemButton
          key="lift"
          asset={asset}
          editable={editable}
          afterUpdate={afterUpdate}
        />
      )}

      {children}

      {fields.includes('photo') && (
        <AssetPhotoListItem key="photo" asset={asset} editable={editable} onUpdate={afterUpdate} />
      )}

      {fields.includes('attachedAt') && (
        <AssetAttachedAtListItem
          key="attachedAt"
          asset={asset}
          editable={editable}
          afterSubmit={afterUpdate}
        />
      )}

      {fields.includes('detachedAt') && (
        <AssetDetachedAtListItem
          key="detachedAt"
          asset={asset}
          editable={editable}
          afterSubmit={afterUpdate}
        />
      )}
    </List>
  )
}

interface AssetPhotoListItemProps extends Omit<ListItemProps, 'children'> {
  asset: Asset
  editable?: boolean
  onUpdate?: (asset: Asset) => void
}

export function AssetPhotoListItem({
  asset,
  editable = true,
  onUpdate,
  ...props
}: AssetPhotoListItemProps) {
  const { t } = useTranslation('asset')

  const { canEditAsset } = usePolicy()

  return (
    <ListItem {...props}>
      {asset?.photoUrl ? (
        <>
          <ImageWithPreview
            src={asset.photoUrl}
            alt={asset.name}
            style={{
              display: 'block',
              objectFit: 'contain',
              width: 'auto',
              height: '64px',
              maxWidth: '96px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            previewMaxWidth="lg"
          />

          {editable && canEditAsset(asset) && (
            <ListItemSecondaryAction>
              <Stack direction="row" gap={1}>
                <AssetPhotoDeleteButton asset={asset} onUpdate={onUpdate} />
                <AssetPhotoUploadButton asset={asset} onUpdate={onUpdate} component={IconButton} />
              </Stack>
            </ListItemSecondaryAction>
          )}
        </>
      ) : (
        <>
          {editable && (
            <ListItemText
              primary={t('noPhoto')}
              secondary={t('photo')}
              primaryTypographyProps={{ color: 'text.disabled' }}
            />
          )}
          {editable && canEditAsset(asset) && (
            <ListItemSecondaryAction>
              <AssetPhotoUploadButton asset={asset} onUpdate={onUpdate} component={Button} />
            </ListItemSecondaryAction>
          )}
        </>
      )}
    </ListItem>
  )
}

interface AssetAttachedAtListItemProps extends Omit<ListItemProps, 'children'> {
  asset: Asset
  editable?: boolean
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

export function AssetAttachedAtListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetAttachedAtListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The attachment timestamps.
   */
  const timestamps = useAssetTimestamps(asset)
  const attachedAt = timestamps?.attachedAt
  const attachedAtValue = asset?.attachedAt || attachedAt
  const attachedAtText = attachedAtValue ? humanizeTimestamp(attachedAtValue) : '-'

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    attachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  if (!isSuperadmin) {
    return null
  }

  return (
    <>
      <ListItem key="asset.attachedAt" disableGutters disablePadding {...props}>
        <CopyableListItemButton copyText={attachedAtValue ? attachedAtText : ''}>
          <ListItemText primary={attachedAtText} secondary={t('asset:attachedAt')} />
          {editable && (
            <ListItemSecondaryAction>
              <Tooltip title={t('edit') as string}>
                <IconButton onClick={openDialog}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          )}
        </CopyableListItemButton>
      </ListItem>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('asset:selectAttachedAt')}</DialogTitle>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <DialogContent>
                <Stack direction="row" alignItems="center" gap={3}>
                  <DatePicker
                    label={t('date')}
                    value={values?.attachedAt ?? null}
                    onChange={(value) => {
                      if (
                        value === null ||
                        asset?.detachedAt === undefined ||
                        asset?.detachedAt === null ||
                        isBefore(value, new Date(asset.detachedAt))
                      ) {
                        setFieldValue('attachedAt', value)
                      }
                    }}
                  />
                  <TimePicker
                    label={t('time')}
                    value={values?.attachedAt ?? null}
                    sx={{ width: '100px' }}
                    onChange={(value) => {
                      if (
                        value === null ||
                        asset?.detachedAt === undefined ||
                        asset?.detachedAt === null ||
                        isBefore(value, new Date(asset.detachedAt))
                      ) {
                        setFieldValue('attachedAt', value)
                      }
                    }}
                    // @ts-expect-error typed incorrectly
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button variant="text" color="inherit" onClick={closeDialog}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

interface AssetDetachedAtListItemProps extends Omit<ListItemProps, 'children'> {
  asset: Asset
  editable?: boolean
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

export function AssetDetachedAtListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetDetachedAtListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  const timestamps = useAssetTimestamps(asset)

  const detachedAt = timestamps?.detachedAt
  const detachedAtText = asset?.detachedAt
    ? humanizeTimestamp(asset.detachedAt)
    : detachedAt
      ? humanizeTimestamp(detachedAt)
      : '-'

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    detachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  return (
    <>
      {isSuperadmin && detachedAt !== null && (
        <>
          <ListItem disableGutters disablePadding {...props}>
            <CopyableListItemButton copyText={detachedAt != null ? detachedAtText : ''}>
              <ListItemText primary={detachedAtText} secondary={t('asset:detachedAt')} />

              {editable && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('edit') as string}>
                    <IconButton onClick={openDialog}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </CopyableListItemButton>
          </ListItem>

          <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>{t('asset:selectDetachedAt')}</DialogTitle>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Stack direction="row" alignItems="center" gap={3}>
                      <DatePicker
                        label={t('date')}
                        value={values?.detachedAt ?? null}
                        onChange={(value: Date | null) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                      />

                      <TimePicker
                        label={t('time')}
                        value={values?.detachedAt ?? null}
                        sx={{ width: '100px' }}
                        onChange={(value) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                        // @ts-expect-error typed incorrectly
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" color="inherit" onClick={closeDialog}>
                      {t('cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('save')}
                    </LoadingButton>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
    </>
  )
}
